import React, { useState } from 'react';
import styled from 'styled-components';
import PageTemplate from '../../components/templates/PageTemplate';
import { MobileHeaderType } from '../../components/layout/ConfigurableMobileHeader';

const PageContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 960px;
  padding: 0 24px;
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
  width: 100%;
`;

const StatCard = styled.div`
  background-color: #1E1B2E;
  border-radius: 8px;
  padding: 16px 20px;
  flex: 1;
  margin: 0 8px;
  &:first-child {
    margin-left: 0;
  }
  &:last-child {
    margin-right: 0;
  }
`;

const StatLabel = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  color: #FFFFFF;
  font-size: 24px;
  font-weight: 600;
`;

const SectionTitle = styled.h2`
  color: #FFFFFF;
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 20px;
`;

const TimeframeContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
`;

const TimeframeButton = styled.button<{ $active: boolean }>`
  background-color: ${({ $active }) => $active ? '#9333EA' : 'rgba(255, 255, 255, 0.05)'};
  color: ${({ $active }) => $active ? '#fff' : 'rgba(255, 255, 255, 0.7)'};
  border: none;
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: ${({ $active }) => $active ? '500' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  
  &:hover {
    background-color: ${({ $active }) => $active ? '#A855F7' : 'rgba(255, 255, 255, 0.1)'};
  }
`;

const MetricContainer = styled.div`
  margin-bottom: 48px;
`;

const MetricHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const MetricTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #FFFFFF;
  text-transform: capitalize;
`;

const MetricChange = styled.div`
  font-size: 14px;
  color: #34C759;
`;

const MetricValues = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const MetricValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #FFFFFF;
`;

const MetricDate = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
`;

const SelectContainer = styled.div`
  position: relative;
  margin-bottom: 32px;
`;

const SelectBox = styled.div`
  background-color: #1E1B2E;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const SelectText = styled.div`
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 500;
`;

const ChevronIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #1E1B2E;
  border-radius: 8px;
  margin-top: 4px;
  overflow: hidden;
  z-index: 10;
`;

const DropdownItem = styled.div`
  padding: 12px 16px;
  display: flex;
  align-items: center;
  cursor: pointer;
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const BrandLogo = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background-color: #9333EA;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-weight: 600;
  font-size: 14px;
  color: white;
`;

const KayakLogo = styled(BrandLogo)`
  background-color: #FF5A1F;
`;

const EmiratesLogo = styled(BrandLogo)`
  background-color: #E02020;
`;

const ChartContainer = styled.div`
  height: 80px;
  width: 100%;
  position: relative;
  margin-top: 16px;
  display: flex;
  align-items: flex-end;
`;

const ChartLine = styled.div`
  position: relative;
  width: 100%;
  height: 2px;
  background-color: rgba(255, 255, 255, 0.1);
  margin-bottom: 20px;
`;

const ChartMarker = styled.div`
  position: absolute;
  width: 24px;
  height: 24px;
  background-color: #9333EA;
  border-radius: 50%;
  opacity: 0.5;
  left: 10%;
  top: -12px;
`;

const Earnings: React.FC = () => {
  const [activeTimeframe, setActiveTimeframe] = useState('4w');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedRelationship, setSelectedRelationship] = useState('All Relationships');
  
  const handleTimeframeChange = (timeframe: string) => {
    setActiveTimeframe(timeframe);
  };
  
  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };
  
  const selectRelationship = (relationship: string) => {
    setSelectedRelationship(relationship);
    setShowDropdown(false);
  };
  
  return (
    <PageTemplate
      mobileHeaderType={MobileHeaderType.SIMPLE}
      mobileHeaderTitle="Earnings"
      showBackButton={true}
      backPath="/wallet"
    >
      <PageContainer>
        <ContentWrapper>
          <StatsContainer>
            <StatCard>
              <StatLabel>14 day</StatLabel>
              <StatValue>$16k</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>30 day</StatLabel>
              <StatValue>$80k</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Wallet</StatLabel>
              <StatValue>$26K</StatValue>
            </StatCard>
          </StatsContainer>
          
          <SectionTitle>Overview</SectionTitle>
          
          <SelectContainer>
            <SelectBox onClick={toggleDropdown}>
              <SelectText>{selectedRelationship}</SelectText>
              <ChevronIcon>
                {showDropdown ? '▲' : '▼'}
              </ChevronIcon>
            </SelectBox>
            {showDropdown && (
              <DropdownContainer>
                <DropdownItem onClick={() => selectRelationship('All Relationships')}>
                  <SelectText>All Relationships</SelectText>
                </DropdownItem>
                <DropdownItem onClick={() => selectRelationship('Blinklink Corp')}>
                  <BrandLogo>B</BrandLogo>
                  <SelectText>Blinklink Corp</SelectText>
                </DropdownItem>
                <DropdownItem onClick={() => selectRelationship('Kayak')}>
                  <KayakLogo>K</KayakLogo>
                  <SelectText>Kayak</SelectText>
                </DropdownItem>
                <DropdownItem onClick={() => selectRelationship('Emirates')}>
                  <EmiratesLogo>E</EmiratesLogo>
                  <SelectText>Emirates</SelectText>
                </DropdownItem>
              </DropdownContainer>
            )}
          </SelectContainer>
          
          <TimeframeContainer>
            <TimeframeButton 
              $active={activeTimeframe === '1w'} 
              onClick={() => handleTimeframeChange('1w')}
            >
              1w
            </TimeframeButton>
            <TimeframeButton 
              $active={activeTimeframe === '4w'} 
              onClick={() => handleTimeframeChange('4w')}
            >
              4w
            </TimeframeButton>
            <TimeframeButton 
              $active={activeTimeframe === '1y'} 
              onClick={() => handleTimeframeChange('1y')}
            >
              1y
            </TimeframeButton>
            <TimeframeButton 
              $active={activeTimeframe === 'mtd'} 
              onClick={() => handleTimeframeChange('mtd')}
            >
              mtd
            </TimeframeButton>
            <TimeframeButton 
              $active={activeTimeframe === 'qtd'} 
              onClick={() => handleTimeframeChange('qtd')}
            >
              qtd
            </TimeframeButton>
            <TimeframeButton 
              $active={activeTimeframe === 'ytd'} 
              onClick={() => handleTimeframeChange('ytd')}
            >
              ytd
            </TimeframeButton>
            <TimeframeButton 
              $active={activeTimeframe === 'all'} 
              onClick={() => handleTimeframeChange('all')}
            >
              all
            </TimeframeButton>
          </TimeframeContainer>
          
          <MetricContainer>
            <MetricHeader>
              <MetricTitle>Earnings</MetricTitle>
              <MetricChange>+0.0%</MetricChange>
            </MetricHeader>
            <MetricValues>
              <div>
                <MetricValue>$0.00</MetricValue>
                <MetricDate>12 Jan - 12 Feb</MetricDate>
              </div>
              <div>
                <MetricValue>$0.00</MetricValue>
                <MetricDate>12 Feb - Today</MetricDate>
              </div>
            </MetricValues>
            <ChartContainer>
              <ChartLine>
                <ChartMarker />
              </ChartLine>
            </ChartContainer>
          </MetricContainer>
          
          <MetricContainer>
            <MetricHeader>
              <MetricTitle>Content Uploads</MetricTitle>
              <MetricChange>+0.0%</MetricChange>
            </MetricHeader>
            <MetricValues>
              <div>
                <MetricValue>124</MetricValue>
                <MetricDate>12 Jan - 12 Feb</MetricDate>
              </div>
              <div>
                <MetricValue>145</MetricValue>
                <MetricDate>12 Feb - Today</MetricDate>
              </div>
            </MetricValues>
            <ChartContainer>
              <ChartLine>
                <ChartMarker />
              </ChartLine>
            </ChartContainer>
          </MetricContainer>
          
          <MetricContainer>
            <MetricHeader>
              <MetricTitle>Views</MetricTitle>
              <MetricChange>+0.0%</MetricChange>
            </MetricHeader>
            <MetricValues>
              <div>
                <MetricValue>15</MetricValue>
                <MetricDate>12 Jan - 12 Feb</MetricDate>
              </div>
              <div>
                <MetricValue>17</MetricValue>
                <MetricDate>12 Feb - Today</MetricDate>
              </div>
            </MetricValues>
            <ChartContainer>
              <ChartLine>
                <ChartMarker />
              </ChartLine>
            </ChartContainer>
          </MetricContainer>
        </ContentWrapper>
      </PageContainer>
    </PageTemplate>
  );
};

export default Earnings;
